<template>
    <aside
        v-show="authenticated && visible"
        :id="previewId"
        ref="panel"
        class="book-quick-preview"
        role="dialog"
        aria-modal="false"
        aria-label="Швидкий перегляд книги"
        :aria-hidden="authenticated && visible ? 'false' : 'true'"
        :style="panelStyle"
        @mouseenter="$emit('enter')"
        @mouseleave="$emit('leave')"
        @focusin="$emit('focus-enter')"
        @focusout="$emit('focus-leave', $event)"
        @mousedown.capture="$emit('enter')"
        @keydown="handleKeydown"
    >
        <div v-if="authenticated && item && context" class="min-w-0">
            <a v-if="bookUrl" :href="bookUrl" class="book-quick-preview__title" data-preview-action>{{ item.book.title }}</a>
            <div v-if="viewerSurface && authenticated" class="mt-2 text-xs font-extrabold text-foxy-text-secondary">Моя бібліотека</div>

            <div v-if="hasGlobalRating" class="mt-2 flex items-center gap-2 text-xs text-foxy-text-secondary">
                <i class="fas fa-star text-foxy-honey" aria-hidden="true"></i>
                <strong class="text-foxy-text">{{ formattedGlobalRating }}</strong>
                <span>{{ ratingCountLabel }}</span>
            </div>

            <div class="mt-3 min-w-0">
                <book-user-state-badge v-if="hasShelfStatus" :status="context.shelfStatus" />
                <div v-if="isPaused" class="mt-1.5 text-xs font-semibold text-foxy-text-secondary">Читання відкладено</div>
                <div v-if="contextDateLabel" class="mt-1 text-xs text-foxy-text-secondary">{{ contextDateLabel }}</div>
                <div v-if="readingMetadata" class="mt-1.5 flex items-start gap-1.5 text-xs text-foxy-text-secondary">
                    <i class="fas fa-info-circle mt-0.5 text-[0.65rem]" aria-hidden="true"></i>
                    <span>{{ readingMetadata }}</span>
                </div>
            </div>

            <div v-if="primaryAction" class="mt-3 flex flex-wrap gap-2" aria-label="Швидкі дії читання">
                <button
                    type="button"
                    class="acc-btn-primary min-h-10 !px-3 !py-2 text-xs"
                    data-preview-action
                    :disabled="busy"
                    @click="runLifecycle(primaryAction)"
                >
                    <i v-if="primaryAction.icon" class="fas" :class="primaryAction.icon" aria-hidden="true"></i>{{ primaryAction.label }}
                </button>
                <button
                    v-if="secondaryAction"
                    type="button"
                    class="acc-btn min-h-10 !px-3 !py-2 text-xs"
                    data-preview-action
                    :disabled="busy"
                    @click="runLifecycle(secondaryAction)"
                >
                    {{ secondaryAction.label }}
                </button>
                <div v-if="overflowActions.length" class="relative">
                    <button
                        ref="overflowTrigger"
                        type="button"
                        class="acc-btn min-h-10 !px-3 !py-2 text-xs"
                        data-preview-action
                        aria-label="Додаткові дії"
                        aria-haspopup="menu"
                        :aria-expanded="overflowOpen ? 'true' : 'false'"
                        :disabled="busy"
                        @click="toggleOverflow"
                    >
                        <i class="fas fa-ellipsis-h" aria-hidden="true"></i>
                    </button>
                    <div
                        v-if="overflowOpen"
                        ref="overflowMenu"
                        class="book-quick-preview__menu"
                        :style="overflowStyle"
                        role="menu"
                        @keydown="handleOverflowKeydown"
                    >
                        <button
                            v-for="action in overflowActions"
                            :key="action.key"
                            type="button"
                            role="menuitem"
                            data-preview-menuitem
                            :class="{ 'text-foxy-error': action.destructive }"
                            @click="runOverflowAction(action)"
                        >
                            {{ action.label }}
                        </button>
                    </div>
                </div>
            </div>

            <div v-if="postCompletionPrompt" class="book-quick-preview__editor" aria-live="polite">
                <div class="text-sm font-extrabold text-foxy-text">Як вам книга?</div>
                <rating-input v-model="ratingDraft" class="mt-2" label="Оцінка завершеного прочитання" :disabled="busy" />
                <div class="mt-3 flex flex-wrap gap-2">
                    <button type="button" class="acc-btn-primary" data-preview-action :disabled="busy || ratingDraft === null" @click="saveRating">
                        Зберегти
                    </button>
                    <button type="button" class="acc-btn" data-preview-action :disabled="busy" @click="$emit('skip-completion-rating')">
                        Пропустити
                    </button>
                </div>
            </div>

            <div v-else-if="context.owned" class="book-quick-preview__rows">
                <div v-if="context.actionReading" class="book-quick-preview__row">
                    <span class="min-w-0 truncate text-foxy-text-secondary"
                        >Моя оцінка · <strong class="text-foxy-text">{{ personalRatingLabel }}</strong></span
                    >
                    <button type="button" class="book-quick-preview__row-action" data-preview-action :disabled="busy" @click="openRatingEditor">
                        {{ context.actionReading.rating === null ? 'Оцінити' : 'Змінити' }}
                    </button>
                </div>
                <div v-if="ratingEditing" class="book-quick-preview__editor !mt-0 !rounded-none !border-x-0 !border-b-0">
                    <rating-input v-model="ratingDraft" label="Моя оцінка" :disabled="busy" />
                    <p v-if="ratingError" class="mt-2 text-xs text-foxy-error" role="alert">{{ ratingError }}</p>
                    <div class="mt-3 flex flex-wrap gap-2">
                        <button
                            type="button"
                            class="acc-btn-primary"
                            data-preview-action
                            :disabled="busy || ratingDraft === null"
                            @click="saveRating"
                        >
                            Зберегти
                        </button>
                        <button type="button" class="acc-btn" data-preview-action :disabled="busy" @click="cancelRatingEditor">Скасувати</button>
                        <button
                            v-if="canClearRating"
                            type="button"
                            class="min-h-10 px-2 text-xs font-extrabold text-foxy-error underline underline-offset-4"
                            data-preview-action
                            :disabled="busy"
                            @click="$emit('rating-clear')"
                        >
                            Прибрати оцінку
                        </button>
                    </div>
                </div>
                <div class="book-quick-preview__row">
                    <span class="min-w-0 truncate text-foxy-text-secondary">{{ collectionsLabel }}</span>
                    <button type="button" class="book-quick-preview__row-action" data-preview-action :disabled="busy" @click="$emit('collections')">
                        {{ context.collectionCount > 0 ? 'Змінити' : 'Додати' }}
                    </button>
                </div>
            </div>

            <div v-if="viewerSurface && authenticated && !context.owned && context.historySummary" class="mt-3 text-xs text-foxy-text-secondary" data-preview-history-summary>
                <div class="font-extrabold">Моя історія</div>
                <div v-if="historySummaryLabel" class="mt-1">{{ historySummaryLabel }}</div>
                <div v-if="context.historySummary.latestHistory && context.historySummary.latestHistory.displayDateLabel" class="mt-1">
                    {{ context.historySummary.latestHistory.displayDateLabel }}
                </div>
                <div v-if="context.historySummary.latestHistory && context.historySummary.latestHistory.rating !== null" class="mt-1">
                    Моя оцінка · <strong class="text-foxy-text">{{ context.historySummary.latestHistory.rating }}/10</strong>
                </div>
            </div>

            <button
                v-if="viewerSurface && authenticated && !context.owned"
                type="button"
                class="acc-btn-primary mt-3 min-h-10 !px-3 !py-2 text-xs"
                data-preview-action
                :disabled="busy"
                aria-haspopup="dialog"
                @click="$emit('add', $event.currentTarget)"
            >
                Додати до бібліотеки
            </button>
            <a
                v-if="viewerSurface && context.owned && context.managementUrl"
                :href="context.managementUrl"
                class="mt-3 block text-xs font-extrabold text-foxy-action hover:underline"
                data-preview-action
                @click.prevent="$emit('manage', {})"
            >
                Керувати
            </a>

            <p v-if="error" class="mt-3 text-xs font-semibold text-foxy-error" role="alert">{{ error }}</p>

            <div v-if="renderedPublicationAction" class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs">
                <a :href="renderedPublicationAction.url" class="font-extrabold text-foxy-action hover:underline" data-preview-action>
                    {{ renderedPublicationAction.label }}
                </a>
            </div>
        </div>
    </aside>
</template>

<script>
import BookUserStateBadge from './BookUserStateBadge.vue';
import RatingInput from '../RatingInput.vue';
import { quickPreviewActionMatrix } from '../../utils/quickPreviewActions';
import { isVisibleBookStatus } from '../../utils/bookStatusPresentation';

const MENU_PADDING = 12;
const MENU_GAP = 6;

export default {
    name: 'BookQuickPreview',
    components: { BookUserStateBadge, RatingInput },
    props: {
        item: { type: Object, default: null },
        context: { type: Object, default: null },
        visible: { type: Boolean, default: false },
        previewId: { type: String, required: true },
        panelStyle: { type: Object, default: () => ({}) },
        busy: { type: Boolean, default: false },
        error: { type: String, default: '' },
        postCompletionPrompt: { type: Boolean, default: false },
        viewerSurface: { type: Boolean, default: false },
        authenticated: { type: Boolean, default: false },
    },
    data() {
        return {
            overflowOpen: false,
            overflowStyle: { left: '12px', top: '12px', visibility: 'hidden' },
            ratingEditing: false,
            ratingDraft: null,
            ratingError: '',
        };
    },
    computed: {
        historySummaryLabel() {
            const summary = this.context?.historySummary;
            if (!summary) return '';
            const facts = [];
            if (summary.completedReadingsCount) facts.push(
                `${summary.completedReadingsCount} ${this.plural(summary.completedReadingsCount, 'прочитання', 'прочитання', 'прочитань')}`,
            );
            if (summary.abandonedReadingsCount) facts.push(`Не дочитано: ${summary.abandonedReadingsCount}`);
            if (summary.readingRecordsCount) facts.push(`Записи читання: ${summary.readingRecordsCount}`);
            if (summary.archivedSessionsCount) facts.push(`Архівні записи: ${summary.archivedSessionsCount}`);
            if (summary.legacyCompletedReadingsCount) facts.push(`Заявлено прочитань: ${summary.legacyCompletedReadingsCount}`);
            return facts.join(' · ');
        },
        hasShelfStatus() {
            return isVisibleBookStatus(this.context?.shelfStatus);
        },
        actionMatrix() {
            return quickPreviewActionMatrix(this.context || {});
        },
        primaryAction() {
            return this.actionMatrix.primary;
        },
        secondaryAction() {
            return this.actionMatrix.secondary;
        },
        overflowActions() {
            const actions = this.actionMatrix.overflow.map((action) => ({ ...action, destructive: action.key === 'abandon' }));
            if (this.context?.owned) {
                actions.push(
                    this.context.quickRemovalAllowed
                        ? { key: 'remove', label: 'Прибрати з бібліотеки', destructive: true }
                        : { key: 'manage-removal', label: 'Керувати видаленням', destructive: false },
                );
            }
            return actions;
        },
        bookUrl() {
            return this.item?.book?.slug ? `/books/${this.item.book.slug}` : '';
        },
        hasGlobalRating() {
            return Number(this.item?.book?.rating || 0) > 0 && Number(this.item?.book?.ratings_count || 0) > 0;
        },
        formattedGlobalRating() {
            return Number(this.item?.book?.rating || 0).toFixed(1);
        },
        ratingCountLabel() {
            const count = Number(this.item?.book?.ratings_count || 0);
            return `(${count} ${this.plural(count, 'оцінка', 'оцінки', 'оцінок')})`;
        },
        isPaused() {
            return this.context?.actionReading?.state === 'paused';
        },
        contextDateLabel() {
            if (this.context?.shelfStatus === 'read' && !this.isPaused) return '';
            return this.context?.actionReading?.display_date_label || '';
        },
        readingMetadata() {
            const reading = this.context?.actionReading || this.context?.reading;
            if (!reading) return this.context?.completedReadingsCount > 1 ? this.completedReadingsLabel : '';
            const languages = reading.languages || [];
            const formats = reading.formats || [];
            const facts = [];
            if (languages.length === 1) facts.push(languages[0].label);
            else if (languages.length > 1) facts.push(`${languages.length} ${this.plural(languages.length, 'мова', 'мови', 'мов')}`);
            if (formats.length === 1) facts.push(formats[0].label);
            else if (formats.length > 1) facts.push(`${formats.length} ${this.plural(formats.length, 'формат', 'формати', 'форматів')}`);
            if (this.context.completedReadingsCount > 1) facts.push(this.completedReadingsLabel);
            return facts.join(' · ');
        },
        completedReadingsLabel() {
            const count = this.context?.completedReadingsCount || 0;
            return `${count} ${this.plural(count, 'прочитання', 'прочитання', 'прочитань')}`;
        },
        personalRatingLabel() {
            const rating = this.context?.actionReading?.rating;
            return Number.isInteger(rating) ? `${rating}/10` : 'Не оцінено';
        },
        canClearRating() {
            return Number.isInteger(this.context?.actionReading?.rating) && this.context?.actionReading?.ratingClear?.allowed === true;
        },
        collectionsLabel() {
            const count = Number(this.context?.collectionCount || 0);
            return count > 0 ? `Добірки · ${count}` : 'Добірки';
        },
        renderedPublicationAction() {
            return this.context?.publicationAction?.kind === 'manage' ? null : this.context?.publicationAction;
        },
    },
    watch: {
        visible(value) {
            if (!value) this.resetTransientState();
        },
        item() {
            this.resetTransientState();
        },
        postCompletionPrompt(value) {
            if (value) {
                this.ratingEditing = false;
                this.ratingDraft = null;
                this.$nextTick(() => this.$emit('layout-change'));
            }
        },
    },
    methods: {
        plural(count, one, few, many) {
            const value = Math.abs(Number(count)) % 100;
            const last = value % 10;
            if (value > 10 && value < 20) return many;
            if (last === 1) return one;
            if (last >= 2 && last <= 4) return few;
            return many;
        },
        resetTransientState() {
            this.overflowOpen = false;
            this.ratingEditing = false;
            this.ratingDraft = null;
            this.ratingError = '';
        },
        runLifecycle(action) {
            if (!this.busy && action) this.$emit('lifecycle', action.key);
        },
        runOverflowAction(action) {
            this.closeOverflowMenu();
            if (action.key === 'remove') this.$emit('remove');
            else if (action.key === 'manage-removal') this.$emit('manage', { section: null, reason: 'removal' });
            else this.runLifecycle(action);
        },
        openRatingEditor() {
            this.ratingDraft = this.context?.actionReading?.rating ?? null;
            this.ratingError = '';
            this.ratingEditing = true;
            this.$nextTick(() => this.$emit('layout-change'));
        },
        cancelRatingEditor() {
            this.ratingEditing = false;
            this.ratingDraft = null;
            this.ratingError = '';
            this.$nextTick(() => this.$emit('layout-change'));
        },
        saveRating() {
            if (!Number.isInteger(this.ratingDraft)) {
                this.ratingError = 'Оберіть оцінку від 1 до 10.';
                return;
            }
            this.ratingError = '';
            this.$emit('rating-save', this.ratingDraft);
        },
        toggleOverflow() {
            if (this.overflowOpen) return this.closeOverflowMenu();
            this.overflowOpen = true;
            this.$nextTick(() => {
                this.positionOverflowMenu();
                this.$nextTick(() => this.menuItems()[0]?.focus());
            });
        },
        positionOverflowMenu() {
            const trigger = this.$refs.overflowTrigger;
            const menu = this.$refs.overflowMenu;
            if (!trigger || !menu) return;
            const rect = trigger.getBoundingClientRect();
            const width = menu.offsetWidth || 224;
            const height = menu.offsetHeight || 160;
            let left = rect.right - width;
            left = Math.max(MENU_PADDING, Math.min(left, window.innerWidth - width - MENU_PADDING));
            let top = rect.bottom + MENU_GAP;
            if (top + height > window.innerHeight - MENU_PADDING) top = rect.top - height - MENU_GAP;
            top = Math.max(MENU_PADDING, Math.min(top, window.innerHeight - height - MENU_PADDING));
            this.overflowStyle = { left: `${Math.round(left)}px`, top: `${Math.round(top)}px`, visibility: 'visible' };
        },
        closeOverflowMenu({ restoreFocus = false } = {}) {
            if (!this.overflowOpen) return;
            this.overflowOpen = false;
            if (restoreFocus) this.$nextTick(() => this.$refs.overflowTrigger?.focus());
        },
        menuItems() {
            return Array.from(this.$refs.overflowMenu?.querySelectorAll('[data-preview-menuitem]') || []);
        },
        handleOverflowKeydown(event) {
            const items = this.menuItems();
            if (!items.length) return;
            const current = Math.max(0, items.indexOf(document.activeElement));
            let next = null;
            if (event.key === 'ArrowDown') next = (current + 1) % items.length;
            if (event.key === 'ArrowUp') next = (current - 1 + items.length) % items.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = items.length - 1;
            if (next !== null) {
                event.preventDefault();
                items[next].focus();
            }
        },
        focusFirstAction() {
            this.$nextTick(() => this.$refs.panel?.querySelector('[data-preview-action]')?.focus());
        },
        handleKeydown(event) {
            if (event.key === 'Escape' && this.overflowOpen) {
                event.preventDefault();
                event.stopPropagation();
                this.closeOverflowMenu({ restoreFocus: true });
                return;
            }
            if (event.key !== 'Tab') return;
            const actions = Array.from(
                this.$refs.panel?.querySelectorAll('a[href], button, input:not([type="hidden"]), select, textarea, [tabindex]') || [],
            ).filter((element) => !element.disabled && element.tabIndex !== -1 && element.offsetParent !== null);
            if (event.shiftKey && actions[0] === document.activeElement) {
                event.preventDefault();
                this.$emit('return-to-trigger');
            } else if (!event.shiftKey && actions[actions.length - 1] === document.activeElement) {
                event.preventDefault();
                this.$emit('continue-forward');
            }
        },
    },
};
</script>

<style scoped>
.book-quick-preview {
    position: fixed;
    z-index: 80;
    width: min(23rem, calc(100vw - 2rem));
    max-height: calc(100vh - 2rem);
    overflow: auto;
    border: 1px solid rgb(var(--foxy-border));
    border-radius: 1rem;
    padding: 0.875rem;
    background: rgb(var(--foxy-elevated) / 0.98);
    color: rgb(var(--foxy-text));
    box-shadow: 0 20px 55px rgb(var(--foxy-cocoa) / 0.28);
}
.book-quick-preview__title {
    display: block;
    overflow: hidden;
    color: rgb(var(--foxy-text));
    font-size: 1rem;
    font-weight: 800;
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.book-quick-preview__title:hover,
.book-quick-preview__title:focus-visible,
.book-quick-preview__row-action:hover,
.book-quick-preview__row-action:focus-visible {
    color: rgb(var(--foxy-action));
}
.book-quick-preview__rows {
    margin-top: 0.75rem;
    overflow: hidden;
    border: 1px solid rgb(var(--foxy-border));
    border-radius: 0.85rem;
    background: rgb(var(--foxy-surface) / 0.7);
}
.book-quick-preview__row {
    display: flex;
    min-height: 2.75rem;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.6rem 0.75rem;
    font-size: 0.75rem;
}
.book-quick-preview__row + .book-quick-preview__row,
.book-quick-preview__editor + .book-quick-preview__row {
    border-top: 1px solid rgb(var(--foxy-border));
}
.book-quick-preview__row-action {
    flex: none;
    border: 0;
    background: transparent;
    color: rgb(var(--foxy-action));
    font-size: 0.75rem;
    font-weight: 800;
}
.book-quick-preview__editor {
    margin-top: 0.75rem;
    padding: 0.7rem;
    border: 1px solid rgb(var(--foxy-border));
    border-radius: 0.85rem;
    background: rgb(var(--foxy-surface) / 0.82);
}
.book-quick-preview__menu {
    position: fixed;
    z-index: 82;
    min-width: 14rem;
    max-width: calc(100vw - 1.5rem);
    overflow: hidden;
    border: 1px solid rgb(var(--foxy-border));
    border-radius: 0.75rem;
    background: rgb(var(--foxy-surface));
    box-shadow: 0 14px 34px rgb(var(--foxy-cocoa) / 0.22);
}
.book-quick-preview__menu button {
    display: block;
    width: 100%;
    min-height: 2.75rem;
    padding: 0.7rem 0.85rem;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 700;
    white-space: normal;
}
.book-quick-preview__menu button:hover,
.book-quick-preview__menu button:focus-visible {
    background: rgb(var(--foxy-elevated));
    outline: none;
}
</style>
